import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { unlistedDiscriminantSchema } from "../../../shared/core/discriminated-union-read.schema";
import { enumReadSchema } from "../../../shared/core/enum-read.schema";
import { DRIFT_VERDICT_PROGRAMS } from "../../../shared/domain/vibe/drift-program-line";
import { VibeTenantClusterRosterStateReadSchema } from "../../../shared/domain/vibe/tenant-cluster-roster-state";
import { VibeGitHostStatusReadSchema } from "../vibe/schemas/cluster.schemas";
import {
  VIBE_TELEMETRY_STATUS_READ_VALUES,
  VIBE_TENANT_CLUSTER_STATUS_READ_VALUES,
  VibeTelemetryStatusReadSchema,
  VibeTenantClusterStatusReadSchema
} from "../vibe/schemas/status-read.schemas";

// ============================================================
// Tenant cluster status — mirrors $Enums.VibeTenantClusterStatus
// ============================================================

// The READER vocabulary every Vibe response reads the status through: it leads
// the database, and `status-read-values-lead-the-database.test.ts` holds it there.
export const VIBE_TENANT_CLUSTER_STATUS_VALUES = VIBE_TENANT_CLUSTER_STATUS_READ_VALUES;
export const VibeTenantClusterStatusSchema = VibeTenantClusterStatusReadSchema;

// ============================================================
// Git-host (Forgejo) status — mirrors $Enums.VibeGitHostStatus
// ============================================================

export const VIBE_GIT_HOST_STATUS_VALUES = ["PENDING", "DEPLOYING", "HEALTHY", "FAILED"] as const;
export const VibeGitHostStatusSchema = z.enum(VIBE_GIT_HOST_STATUS_VALUES);

// ============================================================
// Telemetry (collector + Loki) status — mirrors $Enums.VibeTelemetryStatus
// ============================================================

// The READER vocabulary, held ahead of the database the same way as the status.
export const VIBE_TELEMETRY_STATUS_VALUES = VIBE_TELEMETRY_STATUS_READ_VALUES;
export const VibeTelemetryStatusSchema = VibeTelemetryStatusReadSchema;

// ============================================================
// Ingress origin lock — sourced from the Prisma enum VibeOriginLock
// ============================================================

/**
 * Which ingress origin lock a tenant runs (`VibeTenantCluster.originLock`).
 * Sourced from the generated Prisma enum values so the contract cannot drift
 * from the column. Null on the wire = never set, which runs as OPEN.
 */
export const VibeOriginLockSchema = z.enum(DbEnum.VIBE_ORIGIN_LOCK_VALUES);

/** `VibeOriginLockSchema` as a reader parses it: a value this build does not list reads as unlisted. */
export const VibeOriginLockReadSchema = enumReadSchema(DbEnum.VIBE_ORIGIN_LOCK_VALUES);

// ============================================================
// Build executor — sourced from the Prisma enum VibeBuildExecutor
// ============================================================

/**
 * Which executor builds a tenant's app images (`VibeTenantCluster.buildExecutor`).
 * Sourced from the generated Prisma enum values so the contract cannot drift from
 * the column.
 */
export const VibeBuildExecutorSchema = z.enum(DbEnum.VIBE_BUILD_EXECUTOR_VALUES);

/** `VibeBuildExecutorSchema` as a reader parses it: a value this build does not list reads as unlisted. */
export const VibeBuildExecutorReadSchema = enumReadSchema(DbEnum.VIBE_BUILD_EXECUTOR_VALUES);

// ============================================================
// List — operator fleet view of every tenant cluster
// ============================================================

/**
 * One row of the operator fleet view. The full lifecycle picture an
 * operator needs to decide provision/disable — status + region + the
 * DEGRADED reason text + the git-host (Forgejo) convergence + timestamps.
 * Cross-org by design (this is an admin surface), keyed by `organizationId`
 * (the cluster PK — a tenant cluster has no separate id). Pulumi stack
 * outputs are deliberately omitted: infra internals, not list-row material.
 */
/**
 * The node-health verdict as the agent's sweep reports it — see `nodeHealth` on
 * the list item below for what each state means.
 */
export const AdminVibeTenantClusterNodeHealthSchema = z.discriminatedUnion("state", [
  z.object({
    state: z.literal("healthy"),
    assessedAt: z.string().datetime(),
    nodeCount: z.number().int().nonnegative()
  }),
  z.object({
    state: z.literal("degraded"),
    assessedAt: z.string().datetime(),
    code: z.string(),
    reason: z.string()
  }),
  z.object({
    state: z.literal("unobserved"),
    assessedAt: z.string().datetime(),
    code: z.string(),
    reason: z.string()
  }),
  z.object({ state: z.literal("unassessed"), reason: z.string() })
]);

/** The verdict as a reader parses it: a state a newer backend added reads as the server's own word. */
export const AdminVibeTenantClusterNodeHealthReadSchema = z.union([
  AdminVibeTenantClusterNodeHealthSchema,
  z.looseObject({ state: unlistedDiscriminantSchema(AdminVibeTenantClusterNodeHealthSchema) })
]);

/**
 * The cluster's server roll request, on the fleet roster. Null when nobody ever
 * asked this cluster to roll.
 *
 *   - requestedAt / reason: the current generation and why it was asked for.
 *   - settledAt: when the roll for that generation was judged settled, or null.
 *   - pending: true while `settledAt` is null or older than `requestedAt` —
 *     computed by the backend, so the console never re-derives it.
 */
export const AdminVibeTenantClusterServerRollSchema = z.object({
  requestedAt: z.string().datetime(),
  reason: z.string().nullable(),
  settledAt: z.string().datetime().nullable(),
  pending: z.boolean()
});

export const AdminVibeTenantClusterListItemSchema = z.object({
  organizationId: z.string(),
  // The org's display name, so the fleet table reads as names rather than
  // opaque `org_…` ids. Null when the org row is gone (a cluster can outlive a
  // deleted org) — the UI falls back to the id, never a fabricated name.
  // `.catch(null)`: same deploy-skew reasoning as `telemetryStatus` below — a
  // newer SPA against an older backend must degrade, not error the fleet page.
  organizationName: z.string().nullable().catch(null),
  status: VibeTenantClusterStatusSchema,
  /**
   * What the roster should RENDER, derived from `status` + `statusReason` at
   * read time. Never stored — `DEGRADED` is one database status carrying three
   * different facts (a benign pending config diff, a real fault, and "nothing
   * on this row says why"), and only the derivation separates them.
   *
   * `status` stays on the wire beside it and stays the lifecycle truth: every
   * write guard, every action gate and every backend transition keys on it. This
   * field is a rendering axis and nothing else.
   *
   * `.nullable().catch(null)` for the same deploy-skew reason as the fields
   * below, with one difference that matters: a null here is not a blank. The
   * console re-derives it from `status` and `statusReason`, which are both on
   * this same row, using the same pure function the backend stamped it with —
   * so an older backend costs a recomputation, never a missing chip.
   */
  rosterState: VibeTenantClusterRosterStateReadSchema.nullable().catch(null),
  region: z.string(),
  statusReason: z.string().nullable(),
  /**
   * Whether the cluster's most recent drift verdict — whatever its outcome, so a
   * HEALTHY cluster's converged one too — was computed by the provisioner
   * program running now (`classifyDriftVerdictProgram`, over the recorded
   * `lastDriftProgramRevision`, never over `statusReason`). A stored verdict
   * outlives the deploy that computed it, so `different` means "computed by
   * another deploy — re-observe before reading it": the next converge applies
   * the running program's diff, which this verdict may understate. `unread` is
   * a verdict that cannot be attributed — no program recorded for it, or no
   * running program known here — and is never to be read as `current`.
   *
   * Read through `enumReadSchema`, so a value a newer backend adds parses as the
   * server's own word rather than failing the row; the console renders an
   * unlisted value as `unread`, never as `current`. `.catch("unread")`: an older
   * backend omits the key, which is the same unattributable case.
   */
  driftVerdictProgram: enumReadSchema(DRIFT_VERDICT_PROGRAMS).catch("unread"),
  gitHostStatus: VibeGitHostStatusReadSchema.nullable(),
  // `.catch(null)`: the admin SPA runtime-parses this list and deploys
  // independently of the backend — an older backend omitting the key must
  // degrade to "unknown", not error the whole fleet page.
  telemetryStatus: VibeTelemetryStatusSchema.nullable().catch(null),
  gitHostInternalUrl: z.string().nullable(),
  retainUntil: z.string().datetime().nullable(),
  /**
   * When an operator put this cluster's reconciliation on hold, and why. Null =
   * reconciling normally. A paused cluster is NOT converged or drift-checked, so
   * the fleet roster has to show it: a hold someone forgot to release is
   * indistinguishable from a healthy cluster otherwise, and the only other places
   * it surfaces are warn logs and raw SQL.
   *
   * `.catch(null)` on both, matching `telemetryStatus` above: the admin SPA
   * runtime-parses this list and deploys independently of the backend, so an
   * older backend omitting the keys must degrade to "not paused" rather than
   * error the whole fleet page.
   */
  reconcilePausedAt: z.string().datetime().nullable().catch(null),
  reconcilePausedReason: z.string().nullable().catch(null),
  /**
   * When this cluster's Pulumi state was last reconciled against LIVE AWS — a
   * refreshed drift preview or a converge, never the cheap state-only preview.
   *
   * On the roster because it is the one column that separates "this cluster is
   * fine" from "nothing has looked at this cluster with its eyes open". Most
   * reconcile ticks run the state-only check, which cannot see infrastructure
   * changed outside Pulumi, so a green status and a stale watermark together are
   * a cluster nobody can vouch for. Null = no live observation on record.
   *
   * `.catch(null)` for the same reason as the pause fields above: the admin SPA
   * deploys independently of the backend, so an older backend omitting the key
   * must degrade to "unknown" rather than error the whole fleet page.
   */
  lastLiveDriftObservationAt: z.string().datetime().nullable().catch(null),
  /**
   * The tenant's ingress origin lock. Null = never set, which runs as OPEN.
   * `.catch(null)` for the same deploy-skew reason as the fields above.
   */
  originLock: VibeOriginLockReadSchema.nullable().catch(null),
  /**
   * Which executor the tenant's builds are switched to. This is the operator's
   * setting, not a promise about where the next build runs: a tenant switched to
   * CODEBUILD keeps starting builds on Nomad until its `build` stack has
   * converged. `.nullable().catch(null)` for the same deploy-skew reason as the
   * fields above — null means the backend predates the field, never "neither".
   */
  buildExecutor: VibeBuildExecutorReadSchema.nullable().catch(null),
  /**
   * The tenant's node health as the last node-health sweep judged it — WITH the
   * machine code and the reason. Before this field the roster had no node-health
   * column at all, and the only place an UNOBSERVED verdict's cause existed was
   * a warn log: nine clusters read UNOBSERVED every two minutes while the reason
   * (`UnauthorizedOperation` on `ec2:DescribeInstances`) was visible nowhere.
   *
   * - `healthy` / `degraded` / `unobserved` — the sweep's verdict, `assessedAt`
   *   being that sweep. `code` is the provider's own where it gave one;
   * - `unassessed` — no verdict to show, and `reason` says which: the cluster is
   *   not serving, no sweep ran inside the store's ten-minute lifetime, or the
   *   store did not answer. Never a blank.
   *
   * `.nullable().catch(null)` for the deploy-skew reason the fields above give:
   * null here means the backend predates the field, and only that.
   */
  nodeHealth: AdminVibeTenantClusterNodeHealthReadSchema.nullable().catch(null),
  /**
   * The operator-requested server roll, or null when none was ever requested.
   * `.catch(null)` for the deploy-skew reason the fields above give: an older
   * backend omits the key, and the console reads that as "no request" rather
   * than erroring the fleet page.
   */
  serverRoll: AdminVibeTenantClusterServerRollSchema.nullable().catch(null),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

export type VibeOriginLock = z.infer<typeof VibeOriginLockSchema>;
export type VibeBuildExecutor = z.infer<typeof VibeBuildExecutorSchema>;
export type AdminVibeTenantClusterListItem = z.infer<typeof AdminVibeTenantClusterListItemSchema>;
export type AdminVibeTenantClusterServerRoll = z.infer<
  typeof AdminVibeTenantClusterServerRollSchema
>;
